import React, { useEffect, useMemo, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Search, Leaf, Loader2, ShoppingCart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const IVA_RATE = 13;
const POS_DRAFT_KEY = "verdantpos:pos-draft:v1";
const ORDER_CHANNELS = ["Cabadi", "Whatsapp", "Instagram", "Feria", "Ojos Verdes"];
const PAYMENT_METHODS = ["Sinpe", "Efectivo", "Transferencia", "Tarjeta", "Contra entrega", "Otro"];
const DELIVERY_TYPES = [
  ["tienda", "Tienda / retiro"],
  ["envio_domicilio", "Envío domicilio"],
  ["envio_correoscr", "Correos CR"],
  ["envio_encomienda", "Encomienda"],
];

const normalize = (value) => String(value || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const toNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const buildName = (it) => [it.genero, it.especie, it.cultivar, it.variegacion, it["tamaño"]]
  .map((x) => String(x || "").trim())
  .filter((x) => x && normalize(x) !== "no")
  .join(" ").trim() || it.product_id || "Producto";
const catalogPrice = (it) => toNumber(it.precio_sugerido);
const isAvailable = (it) => normalize(it.status) === "disponible" && toNumber(it.stock_qty) > 0;

const readPosDraft = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(POS_DRAFT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const promotionAmount = (promotion, base) => {
  const type = normalize(promotion.discount_type);
  const value = Math.max(toNumber(promotion.discount_value), 0);
  if (type === "percentage") return Math.min(base, base * (value / 100));
  if (type === "fixed") return Math.min(base, value);
  return 0;
};

const Select = ({ value, onChange, children, disabled = false }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    className="h-11 sm:h-10 w-full rounded-md border border-white/20 bg-black/20 px-3 text-base sm:text-sm text-white outline-none disabled:opacity-50"
  >
    {children}
  </select>
);

export default function POSPage() {
  const [initialDraft] = useState(() => readPosDraft());
  const [items, setItems] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotionIds, setSelectedPromotionIds] = useState(Array.isArray(initialDraft.selectedPromotionIds) ? initialDraft.selectedPromotionIds : []);
  const [cart, setCart] = useState(Array.isArray(initialDraft.cart) ? initialDraft.cart : []);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [availability, setAvailability] = useState("all");
  const [customerName, setCustomerName] = useState(String(initialDraft.customerName || ""));
  const [customerPhone, setCustomerPhone] = useState(String(initialDraft.customerPhone || ""));
  const [orderChannel, setOrderChannel] = useState(ORDER_CHANNELS.includes(initialDraft.orderChannel) ? initialDraft.orderChannel : "Cabadi");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.includes(initialDraft.paymentMethod) ? initialDraft.paymentMethod : "Sinpe");
  const [paymentAmount, setPaymentAmount] = useState(initialDraft.paymentAmount ?? 0);
  const [deliveryType, setDeliveryType] = useState(DELIVERY_TYPES.some(([value]) => value === initialDraft.deliveryType) ? initialDraft.deliveryType : "tienda");
  const [includeShipping, setIncludeShipping] = useState(Boolean(initialDraft.includeShipping));
  const [shippingAmount, setShippingAmount] = useState(initialDraft.shippingAmount ?? 0);
  const [applyIva, setApplyIva] = useState(Boolean(initialDraft.applyIva));
  const [saving, setSaving] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  const load = async () => {
    setLoadingCatalog(true);
    try {
      const [catalogRes, promotionsRes] = await Promise.all([
        api.get("/gsheet/catalog"),
        api.get("/gsheet/promotions"),
      ]);
      setItems((Array.isArray(catalogRes.data) ? catalogRes.data : [])
        .filter((it) => String(it.product_id || "").trim())
        .map((it) => ({
          ...it,
          id: String(it.product_id),
          name: buildName(it),
          category: String(it.product_type || "Producto").trim() || "Producto",
          price: catalogPrice(it),
          stock: toNumber(it.stock_qty),
          image: String(it.image_url || "").trim(),
        })));
      setPromotions((Array.isArray(promotionsRes.data) ? promotionsRes.data : [])
        .filter((p) => String(p.promotion_id || "").trim())
        .filter((p) => normalize(p.is_active) !== "false")
        .sort((a, b) => toNumber(a.calculation_order) - toNumber(b.calculation_order)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar el catálogo del POS.");
    } finally {
      setLoadingCatalog(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCart((current) => current.map((line) => {
      const item = items.find((candidate) => candidate.id === line.item_id);
      if (!item) return line;
      const nextCatalogPrice = toNumber(item.price);
      return {
        ...line,
        catalog_price: nextCatalogPrice,
        max_stock: toNumber(item.stock),
      };
    }));
  }, [items]);

  useEffect(() => {
    if (deliveryType === "tienda") {
      setIncludeShipping(false);
      setShippingAmount(0);
    }
  }, [deliveryType]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const draft = {
      cart,
      selectedPromotionIds,
      customerName,
      customerPhone,
      orderChannel,
      paymentMethod,
      paymentAmount,
      deliveryType,
      includeShipping,
      shippingAmount,
      applyIva,
    };
    const hasDraft = cart.length > 0
      || selectedPromotionIds.length > 0
      || customerName.trim()
      || customerPhone.trim()
      || orderChannel !== "Cabadi"
      || paymentMethod !== "Sinpe"
      || toNumber(paymentAmount) > 0
      || deliveryType !== "tienda"
      || includeShipping
      || toNumber(shippingAmount) > 0
      || applyIva;
    try {
      if (hasDraft) window.sessionStorage.setItem(POS_DRAFT_KEY, JSON.stringify(draft));
      else window.sessionStorage.removeItem(POS_DRAFT_KEY);
    } catch {
      // Draft persistence is a UX safeguard; storage failures must not block POS operation.
    }
  }, [cart, selectedPromotionIds, customerName, customerPhone, orderChannel, paymentMethod, paymentAmount, deliveryType, includeShipping, shippingAmount, applyIva]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))], [items]);
  const filtered = items.filter((i) => {
    if (availability === "available" && !isAvailable(i)) return false;
    if (availability === "unavailable" && isAvailable(i)) return false;
    return (category === "All" || i.category === category)
      && normalize(`${i.product_id} ${i.name} ${i.category} ${i.ubicacion_id}`).includes(normalize(search));
  });

  const addToCart = (item) => {
    if (!isAvailable(item)) return;
    setCart((c) => {
      const found = c.find((x) => x.item_id === item.id);
      if (found) {
        if (found.quantity >= item.stock) {
          toast.error(`Solo hay ${item.stock} unidad(es) disponibles.`);
          return c;
        }
        return c.map((x) => x.item_id === item.id ? { ...x, quantity: x.quantity + 1, max_stock: item.stock } : x);
      }
      return [...c, {
        item_id: item.id,
        name: item.name,
        price: item.price,
        catalog_price: item.price,
        quantity: 1,
        max_stock: item.stock,
      }];
    });
  };

  const changeQty = (id, delta) => setCart((c) => c.map((x) => {
    if (x.item_id !== id) return x;
    const next = x.quantity + delta;
    if (next > x.max_stock) {
      toast.error(`Solo hay ${x.max_stock} unidad(es) disponibles.`);
      return x;
    }
    return { ...x, quantity: next };
  }).filter((x) => x.quantity > 0));

  const removeItem = (id) => setCart((c) => c.filter((x) => x.item_id !== id));

  const changeSoldPrice = (id, value) => {
    const next = Number(value);
    setCart((c) => c.map((x) => x.item_id === id ? { ...x, price: Number.isFinite(next) ? Math.max(next, 0) : 0 } : x));
  };

  const togglePromotion = (promotion) => {
    const id = String(promotion.promotion_id);
    if (selectedPromotionIds.includes(id)) {
      setSelectedPromotionIds((ids) => ids.filter((x) => x !== id));
      return;
    }
    const selected = promotions.filter((p) => selectedPromotionIds.includes(String(p.promotion_id)));
    if (selected.length && (normalize(promotion.stackable) === "false" || !selected.every((p) => normalize(p.stackable) !== "false"))) {
      toast.error("Esta promoción no puede combinarse con las seleccionadas.");
      return;
    }
    setSelectedPromotionIds((ids) => [...ids, id]);
  };

  const subtotal = cart.reduce((s, x) => s + x.price * x.quantity, 0);
  const pricing = useMemo(() => {
    const selected = promotions.filter((p) => selectedPromotionIds.includes(String(p.promotion_id)));
    let running = subtotal;
    const applied = selected.map((promotion) => {
      const amount = promotionAmount(promotion, running);
      running = Math.max(running - amount, 0);
      return { promotion, amount };
    });
    const discountAmount = applied.reduce((sum, x) => sum + x.amount, 0);
    const productsAfterDiscount = Math.max(subtotal - discountAmount, 0);
    const hasFreeShipping = selected.some((p) => normalize(p.discount_type) === "free_shipping");
    const effectiveShipping = deliveryType !== "tienda" && includeShipping && !hasFreeShipping ? Math.max(toNumber(shippingAmount), 0) : 0;
    const taxableBase = productsAfterDiscount + effectiveShipping;
    const ivaAmount = applyIva ? taxableBase * (IVA_RATE / 100) : 0;
    return { applied, discountAmount, effectiveShipping, ivaAmount, total: taxableBase + ivaAmount, hasFreeShipping };
  }, [promotions, selectedPromotionIds, subtotal, deliveryType, includeShipping, shippingAmount, applyIva]);

  const numericPayment = Math.max(toNumber(paymentAmount), 0);
  const paymentStatusPreview = normalize(paymentMethod) === "contra entrega" || numericPayment <= 0
    ? "PENDING"
    : numericPayment >= pricing.total
      ? "Cancelled (pagada)"
      : "Parcial";
  const balancePreview = Math.max(pricing.total - (normalize(paymentMethod) === "contra entrega" ? 0 : numericPayment), 0);
  const hasOverpayment = normalize(paymentMethod) !== "contra entrega" && numericPayment > pricing.total + 0.01;
  const missingCustomer = !customerName.trim() || !customerPhone.trim();
  const invalidLinePrice = cart.some((x) => toNumber(x.price) <= 0);
  const hasSaleDraft = cart.length > 0
    || selectedPromotionIds.length > 0
    || customerName.trim()
    || customerPhone.trim()
    || orderChannel !== "Cabadi"
    || paymentMethod !== "Sinpe"
    || toNumber(paymentAmount) > 0
    || deliveryType !== "tienda"
    || includeShipping
    || toNumber(shippingAmount) > 0
    || applyIva;

  const resetSaleDraft = () => {
    if (!hasSaleDraft || saving) return;
    if (typeof window !== "undefined" && !window.confirm("¿Limpiar la venta actual? Se eliminarán el carrito y los datos ingresados de este borrador.")) return;
    try { window.sessionStorage.removeItem(POS_DRAFT_KEY); } catch { /* no-op */ }
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setSelectedPromotionIds([]);
    setOrderChannel("Cabadi");
    setPaymentMethod("Sinpe");
    setDeliveryType("tienda");
    setIncludeShipping(false);
    setShippingAmount(0);
    setApplyIva(false);
    setPaymentAmount(0);
    setLastOrder(null);
    toast.success("Venta reiniciada.");
  };

  const confirmSale = async () => {
    if (!cart.length) return toast.error("El carrito está vacío.");
    if (missingCustomer) return toast.error("Nombre y teléfono del cliente son obligatorios.");
    if (invalidLinePrice) return toast.error("Cada producto debe tener un precio vendido mayor que cero.");
    if (hasOverpayment) return toast.error(`El pago no puede exceder el total de ${money(pricing.total)}.`);
    setSaving(true);
    try {
      const { data } = await api.post("/gsheet/confirm-sale", {
        order_customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        order_channel: orderChannel,
        payment_method: paymentMethod,
        payment_amount: normalize(paymentMethod) === "contra entrega" ? 0 : numericPayment,
        delivery_type: deliveryType,
        promotion_ids: selectedPromotionIds,
        shipping_amount: pricing.effectiveShipping,
        apply_iva: applyIva,
        iva_rate: IVA_RATE,
        lines: cart.map((x) => ({ product_id: x.item_id, product_name: x.name, unit_price: x.price, qty: x.quantity })),
      });
      setLastOrder(data);
      toast.success(`Orden #${data.orderId} registrada correctamente.`);
      try { window.sessionStorage.removeItem(POS_DRAFT_KEY); } catch { /* no-op */ }
      setCart([]);
      setCustomerName("");
      setCustomerPhone("");
      setSelectedPromotionIds([]);
      setOrderChannel("Cabadi");
      setPaymentMethod("Sinpe");
      setDeliveryType("tienda");
      setIncludeShipping(false);
      setShippingAmount(0);
      setApplyIva(false);
      setPaymentAmount(0);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar la venta.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 min-h-[calc(100dvh-7rem)] lg:h-[calc(100dvh-4.25rem)] p-3 sm:p-4 lg:p-6">
      <section className="lg:col-span-7 xl:col-span-8 flex flex-col h-auto lg:h-full overflow-visible lg:overflow-hidden bg-card rounded-2xl border border-border shadow-sm p-3 sm:p-4 lg:p-6 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-3">
          <div>
            <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">Plant Catalog</h2>
            <p className="text-xs text-muted-foreground">Inventario_Plantas · {items.length} productos</p>
          </div>
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search plants..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-11 sm:h-10 text-base sm:text-sm"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pb-3 mb-1 min-w-0">
          <div className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1 min-w-0 sm:flex-1">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-3 py-2 sm:py-1.5 rounded-full text-xs font-semibold whitespace-nowrap min-h-9 ${category === c ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-lg border border-border p-1 bg-card overflow-x-auto w-full sm:w-auto shrink-0">
            <button onClick={() => setAvailability("all")} className={`flex-1 sm:flex-none px-2.5 py-2 sm:py-1 rounded-md text-xs font-semibold whitespace-nowrap ${availability === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>Todos</button>
            <button onClick={() => setAvailability("available")} className={`flex-1 sm:flex-none px-2.5 py-2 sm:py-1 rounded-md text-xs font-semibold whitespace-nowrap ${availability === "available" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>Disponibles</button>
            <button onClick={() => setAvailability("unavailable")} className={`flex-1 sm:flex-none px-2.5 py-2 sm:py-1 rounded-md text-xs font-semibold whitespace-nowrap ${availability === "unavailable" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>No disponibles</button>
          </div>
        </div>

        {loadingCatalog && !items.length ? (
          <div className="min-h-60 lg:flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-3.5 max-h-[52dvh] lg:max-h-none lg:flex-1 overflow-y-auto pr-1 pb-4 mt-2">
            {filtered.map((item) => {
              const available = isAvailable(item);
              return (
                <div key={item.id} className="flex flex-col justify-between bg-card rounded-xl border border-border p-2.5 sm:p-3 hover:border-accent hover:shadow-md min-w-0">
                  <div className="aspect-square w-full rounded-lg overflow-hidden bg-secondary mb-2 flex items-center justify-center">
                    {item.image ? <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" /> : <Leaf className="h-8 w-8 text-muted-foreground" />}
                  </div>
                  <span className="text-[10px] font-semibold text-accent uppercase truncate">{item.category}</span>
                  <p className="text-xs sm:text-sm font-semibold leading-tight line-clamp-2 min-h-[2.2rem]">{item.name}</p>
                  {!available
                    ? <span className="text-[10px] font-bold text-destructive">{item.status || "No disponible"}</span>
                    : <span className="text-[10px] text-muted-foreground">{item.stock} in stock</span>}
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <span className="font-mono font-semibold text-xs sm:text-sm truncate">{money(item.price)}</span>
                    <Button size="icon" className="h-9 w-9 rounded-lg shrink-0" disabled={!available || item.price <= 0} onClick={() => addToCart(item)} aria-label={`Agregar ${item.name}`}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <aside className="lg:col-span-5 xl:col-span-4 flex flex-col h-auto lg:h-full bg-primary text-primary-foreground rounded-2xl shadow-xl border border-primary p-4 sm:p-5 overflow-visible lg:overflow-hidden min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingCart className="h-5 w-5 shrink-0" />
          <h2 className="font-display text-lg font-bold">Venta actual</h2>
          <span className="ml-auto text-xs bg-white/15 px-2 py-0.5 rounded-full whitespace-nowrap">{cart.length} líneas</span>
        </div>

        <div className="max-h-[36dvh] lg:max-h-none lg:flex-1 overflow-y-auto space-y-2 pr-1 my-2 border-y border-white/15 py-3">
          {!cart.length && <p className="text-sm text-primary-foreground/60 text-center py-8">Selecciona productos para iniciar la venta.</p>}
          {cart.map((x) => (
            <div key={x.item_id} className="bg-white/10 rounded-lg p-2.5 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold break-words">{x.name}</p>
                  <p className="text-[11px] text-primary-foreground/60">Catálogo: {money(x.catalog_price)}</p>
                </div>
                <button onClick={() => removeItem(x.item_id)} className="h-9 w-9 flex items-center justify-center shrink-0" aria-label={`Eliminar ${x.name}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                <label className="text-[10px] text-primary-foreground/70">
                  Precio vendido unitario
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    value={x.price}
                    onChange={(e) => changeSoldPrice(x.item_id, e.target.value)}
                    className="mt-1 h-11 sm:h-8 bg-black/20 border-white/20 text-white text-base sm:text-sm"
                  />
                </label>
                <div className="flex items-center justify-between sm:justify-start gap-1 bg-black/20 rounded-lg p-1 min-h-11 sm:min-h-8">
                  <button onClick={() => changeQty(x.item_id, -1)} className="h-9 w-9 sm:h-6 sm:w-6 flex items-center justify-center"><Minus className="h-4 w-4 sm:h-3.5 sm:w-3.5" /></button>
                  <span className="text-sm font-mono w-8 sm:w-5 text-center">{x.quantity}</span>
                  <button onClick={() => changeQty(x.item_id, 1)} className="h-9 w-9 sm:h-6 sm:w-6 flex items-center justify-center"><Plus className="h-4 w-4 sm:h-3.5 sm:w-3.5" /></button>
                </div>
                <span className="text-sm font-mono font-semibold sm:pb-1 text-right sm:text-left">{money(x.price * x.quantity)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3 sm:space-y-2 mb-3 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="space-y-1"><span className="font-semibold">Nombre cliente</span><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="bg-white/10 border-white/20 text-white h-11 sm:h-10 text-base sm:text-sm" /></label>
            <label className="space-y-1"><span className="font-semibold">Teléfono</span><Input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="bg-white/10 border-white/20 text-white h-11 sm:h-10 text-base sm:text-sm" /></label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label>Canal<Select value={orderChannel} onChange={(e) => setOrderChannel(e.target.value)}>{ORDER_CHANNELS.map((v) => <option key={v}>{v}</option>)}</Select></label>
            <label>Entrega<Select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}>{DELIVERY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></label>
          </div>

          {!!promotions.length && (
            <div className="bg-black/20 rounded-lg p-2.5 space-y-2">
              <p className="font-semibold">Descuentos / promociones</p>
              <div className="flex flex-wrap gap-2">
                {promotions.map((p) => {
                  const id = String(p.promotion_id);
                  const selected = selectedPromotionIds.includes(id);
                  const type = normalize(p.discount_type);
                  const value = toNumber(p.discount_value);
                  return (
                    <button key={id} onClick={() => togglePromotion(p)} className={`rounded-full px-2.5 py-2 sm:py-1 border ${selected ? "bg-white text-primary border-white" : "border-white/30"}`}>
                      {p.promotion_name} · {type === "percentage" ? `${value}%` : type === "fixed" ? money(value) : "Envío gratis"}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="bg-black/20 rounded-lg p-2.5 flex items-center gap-2 min-h-11">
              <input type="checkbox" checked={includeShipping} disabled={deliveryType === "tienda"} onChange={(e) => setIncludeShipping(e.target.checked)} />
              <span>{deliveryType === "tienda" ? "Envío no aplica en retiro" : "Sumar envío"}</span>
            </label>
            <Input type="number" min="0" step="100" value={shippingAmount} onChange={(e) => setShippingAmount(e.target.value)} disabled={deliveryType === "tienda" || !includeShipping || pricing.hasFreeShipping} className="bg-black/20 border-white/20 text-white h-11 sm:h-9 text-base sm:text-sm" />
          </div>

          <label className="bg-black/20 rounded-lg p-2.5 flex justify-between items-center min-h-11">
            <span>Aplicar IVA ({IVA_RATE}%)</span>
            <input type="checkbox" checked={applyIva} onChange={(e) => setApplyIva(e.target.checked)} />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label>Método de pago<Select value={paymentMethod} onChange={(e) => { setPaymentMethod(e.target.value); if (normalize(e.target.value) === "contra entrega") setPaymentAmount(0); }}>{PAYMENT_METHODS.map((v) => <option key={v}>{v}</option>)}</Select></label>
            <label>Monto recibido<Input type="number" min="0" step="100" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} disabled={normalize(paymentMethod) === "contra entrega"} className={`mt-1 bg-black/20 text-white h-11 sm:h-10 text-base sm:text-sm ${hasOverpayment ? "border-red-400" : "border-white/20"}`} /></label>
          </div>

          <div className={`rounded-lg p-2.5 ${hasOverpayment ? "bg-red-500/25" : "bg-black/20"}`}>
            <div className="flex flex-col xs:flex-row gap-1 justify-between"><span>Estado financiero</span><strong>{hasOverpayment ? "ERROR: sobrepago" : paymentStatusPreview}</strong></div>
            <div className="flex justify-between gap-3 mt-1"><span>Saldo después del pago</span><span className="font-mono whitespace-nowrap">{money(balancePreview)}</span></div>
            {hasOverpayment && <p className="mt-1 text-red-100">El monto recibido no puede superar el total de la orden.</p>}
          </div>
        </div>

        <div className="space-y-1.5 text-sm border-t border-white/15 pt-3">
          <Row label="Subtotal productos" value={money(subtotal)} />
          {pricing.applied.map(({ promotion, amount }) => <Row key={promotion.promotion_id} label={promotion.promotion_name} value={`- ${money(amount)}`} />)}
          {pricing.discountAmount > 0 && <Row label="Total descuentos" value={`- ${money(pricing.discountAmount)}`} />}
          <Row label="Envío" value={money(pricing.effectiveShipping)} />
          <Row label={`IVA (${applyIva ? IVA_RATE : 0}%)`} value={money(pricing.ivaAmount)} />
          <div className="flex justify-between items-center gap-3 pt-2 border-t border-white/20">
            <span className="font-display font-bold text-base">Total</span>
            <span className="font-mono font-bold text-lg sm:text-xl text-right">{money(pricing.total)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={resetSaleDraft}
            disabled={saving || !hasSaleDraft}
            className="min-h-11 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Limpiar venta
          </Button>
          <Button
            onClick={confirmSale}
            disabled={saving || !cart.length || missingCustomer || invalidLinePrice || hasOverpayment}
            className="min-h-11 bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Confirmar venta
          </Button>
        </div>
      </aside>

      <Dialog open={!!lastOrder} onOpenChange={(open) => !open && setLastOrder(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader><DialogTitle>Orden registrada</DialogTitle></DialogHeader>
          {lastOrder && (
            <div className="space-y-2 text-sm">
              <p>Orden <strong>#{lastOrder.orderId}</strong></p>
              <RowDark label="Subtotal" value={money(lastOrder.subtotalAmount)} />
              <RowDark label="Descuentos" value={money(lastOrder.discountAmount)} />
              <RowDark label="Envío" value={money(lastOrder.shippingAmount)} />
              <RowDark label="IVA" value={money(lastOrder.ivaAmount)} />
              <RowDark label="Total" value={money(lastOrder.totalAmount)} />
              <RowDark label="Pagado" value={money(lastOrder.paymentAmount)} />
              <RowDark label="Saldo" value={money(lastOrder.balanceDue)} />
              <p>Estado financiero: <strong>{lastOrder.paymentStatus}</strong></p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 text-primary-foreground/85">
    <span>{label}</span>
    <span className="font-mono text-right whitespace-nowrap">{value}</span>
  </div>
);

const RowDark = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono text-right whitespace-nowrap">{value}</span>
  </div>
);